import { AccountInfo, TokenClaims } from "@azure/msal-common"
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { RootState } from "../../reduxStore/store"
import { default as msalInstance } from "./msalClient/msal"
import { MSAL_DEEP_LINK_URL } from "../dataFetchingRouter/helpers"
import { BASE_ROUTE } from "../../routes"

export enum AuthStatus {
  Authenticated = "authenticated",
  Unauthenticated = "unauthenticated",
  Authenticating = "authenticating",
  Unauthenticating = "unauthenticating",
}

type AuthStatuses = AuthStatus

export type VeapAccountInfo = {
  homeAccountId: string
  environment: string
  tenantId: string
  username: string
  localAccountId: string
  name?: string
  idToken?: string
  idTokenClaims?: TokenClaims & {
    [key: string]: string | number | string[] | object | undefined | unknown
  }
  nativeAccountId?: string
  authorityType?: string
}

export function msalToVeapInfo(account: AccountInfo): VeapAccountInfo {
  return {
    homeAccountId: account.homeAccountId,
    environment: account.environment,
    tenantId: account.tenantId,
    username: account.username,
    localAccountId: account.localAccountId,
    name: account.name,
    idToken: account.idToken,
    idTokenClaims: account.idTokenClaims,
  }
}

export interface AuthState {
  authStatus: AuthStatuses
  msalInitialized: boolean
  accountInfo: VeapAccountInfo | undefined
}

const initialState: AuthState = {
  authStatus: AuthStatus.Authenticating,
  msalInitialized: false,
  accountInfo: undefined,
}

export const initializeSession = createAsyncThunk(
  `authentication/initializeSession`,
  async (_arg, { dispatch }) => {
    // Store any deeplink/query params before initializing so that it is available for initial routing
    // remove after initial navigation (see useNavigationOrchestrator.ts)
    const deepLinkFullUrl = location.pathname + location.search
    if (deepLinkFullUrl.split(BASE_ROUTE).filter(Boolean).length > 0) {
      localStorage.setItem(MSAL_DEEP_LINK_URL, deepLinkFullUrl)
    }

    dispatch(authSlice.actions.authenticating())

    // Similar to authConfig.js
    // Must handle msal initialization at module load
    if (process.env.NODE_ENV == "test") {
      dispatch(authSlice.actions.msalInitialized())
      dispatch(authSlice.actions.authenticated(undefined))
      return undefined
    }
    try {
      await msalInstance.initialize()
      dispatch(authSlice.actions.msalInitialized())
    } catch {
      dispatch(authSlice.actions.unauthenticated())
    }
  },
)

/**
 * Initialize session WITHOUT storing deep link.
 * Use this when you just need to ensure MSAL is initialized
 * but don't want to pollute the deep link handler.
 */
export const ensureSessionInitialized = createAsyncThunk(
  `authentication/ensureSessionInitialized`,
  async (_arg, { dispatch, getState }) => {
    const state = getState() as { authentication: AuthState }

    // If already initialized, do nothing
    if (state.authentication.msalInitialized) {
      return
    }

    dispatch(authSlice.actions.authenticating())

    // Similar to authConfig.js
    // Must handle msal initialization at module load
    if (process.env.NODE_ENV == "test") {
      dispatch(authSlice.actions.msalInitialized())
      dispatch(authSlice.actions.authenticated(undefined))
      return undefined
    }
    try {
      await msalInstance.initialize()
      dispatch(authSlice.actions.msalInitialized())
    } catch {
      dispatch(authSlice.actions.unauthenticated())
    }
  },
)

export const signUpSession = createAsyncThunk(
  `authentication/signUpSession`,
  async (_arg, { dispatch }) => {
    dispatch(authSlice.actions.authenticating())
    try {
      await msalInstance.triggerSignUpRedirect()
    } catch {
      dispatch(authSlice.actions.unauthenticated())
    }
  },
)

export const loginSession = createAsyncThunk(
  `authentication/loginSession`,
  async (_arg, { dispatch }) => {
    dispatch(authSlice.actions.authenticating())
    try {
      await msalInstance.triggerLoginRedirect()
    } catch {
      dispatch(authSlice.actions.unauthenticated())
    }
  },
)

export const totpLoginSession = createAsyncThunk(
  `authentication/totpLoginSession`,
  async (_arg, { dispatch }) => {
    dispatch(authSlice.actions.authenticating())
    try {
      await msalInstance.triggerTotpLoginRedirect()
    } catch {
      dispatch(authSlice.actions.unauthenticated())
    }
  },
)

export const haLoginSession = createAsyncThunk(
  `authentication/haLoginSession`,
  async (caseId: string, { dispatch }) => {
    dispatch(authSlice.actions.authenticating())
    try {
      await msalInstance.triggerHaLoginRedirect(caseId)
    } catch {
      dispatch(authSlice.actions.unauthenticated())
    }
  },
)

export const logoutSession = createAsyncThunk(
  `authentication/logoutSession`,
  async (_arg, { dispatch }) => {
    sessionStorage.clear()
    dispatch(authSlice.actions.unauthenticating())
    try {
      await msalInstance.triggerLogout()
    } catch {
      dispatch(authSlice.actions.unauthenticated())
    }
  },
)

const authSlice = createSlice({
  name: "authentication",
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.authStatus = AuthStatus.Authenticated
      state.accountInfo = action.payload
    },

    unauthenticated: (state) => {
      state.authStatus = AuthStatus.Unauthenticated
      state.accountInfo = undefined
    },

    authenticating: (state) => {
      state.authStatus = AuthStatus.Authenticating
    },

    unauthenticating: (state) => {
      state.authStatus = AuthStatus.Unauthenticating
    },

    msalInitialized: (state) => {
      state.msalInitialized = true
    },
  },
})

export default authSlice.reducer

export const { authenticated, unauthenticated, msalInitialized } =
  authSlice.actions

export const selectAuthenticationStatus = (state: RootState): AuthStatuses =>
  state.authentication.authStatus

export const selectMsalInitialized = (state: RootState): boolean =>
  state.authentication.msalInitialized

export const selectAccountGuid = (state: RootState): string | undefined => {
  // unique care connect directory object ID GUID for account
  return state.authentication.accountInfo?.localAccountId
}
export const selectEmailAddress = (state: RootState): string | undefined => {
  const emails: string[] | undefined =
    state.authentication.accountInfo?.idTokenClaims?.emails
  return emails ? emails[0] : ""
}
export const selectSSOGivenName = (state: RootState): string | undefined => {
  return state.authentication.accountInfo?.idTokenClaims?.given_name as
    | string
    | ""
}
export const selectSSOSurname = (state: RootState): string | undefined => {
  return state.authentication.accountInfo?.idTokenClaims?.family_name as
    | string
    | ""
}
export const selectUserToken = (state: RootState): string | undefined => {
  return state.authentication.accountInfo?.idToken
}

export const selectIsTokenExpired = (state: RootState): boolean | undefined => {
  const idTokenClaims = state.authentication.accountInfo?.idTokenClaims
  return idTokenClaims?.exp ? idTokenClaims.exp * 1000 < Date.now() : undefined
}
